import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, KeyRound, Trash2, CheckCircle2, XCircle, Bell, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SearchInput } from '@/components/shared/SearchInput';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/shared/Pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGetUsers, useDeleteUser } from '@/services/api/endpoints/UserService';
import { useDebounce } from '@/hooks/useDebounce';
import { formatDate } from '@/lib/utils';
import { USER_ROLES } from '@/constants';
import { UserModal } from './UserModal';
import { ResetPasswordModal } from './ResetPasswordModal';
import { SendNotificationModal } from './SendNotificationModal';
import { TopUpWalletModal } from './TopUpWalletModal';
import type { User } from '@/types';

const ALL_FILTER_VALUE = '__all__';

export function UsersPage() {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState(ALL_FILTER_VALUE);
  const [statusFilter, setStatusFilter] = useState(ALL_FILTER_VALUE);
  const [betaFilter, setBetaFilter] = useState(ALL_FILTER_VALUE);
  const debouncedSearch = useDebounce(search);

  // Modal state
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [notificationUser, setNotificationUser] = useState<User | null>(null);
  const [topUpModalOpen, setTopUpModalOpen] = useState(false);
  const [topUpUser, setTopUpUser] = useState<User | null>(null);

  const deleteUser = useDeleteUser();

  const { data, isLoading } = useGetUsers(
    page,
    20,
    debouncedSearch,
    'createdAt',
    'desc',
    roleFilter !== ALL_FILTER_VALUE ? roleFilter : undefined,
    undefined,
    statusFilter !== ALL_FILTER_VALUE ? statusFilter : undefined,
    betaFilter === 'beta' ? true : betaFilter === 'notBeta' ? false : undefined
  );

  const handleOpenCreate = () => {
    setEditingUser(null);
    setUserModalOpen(true);
  };

  const handleOpenEdit = (user: User) => {
    setEditingUser(user);
    setUserModalOpen(true);
  };

  const handleOpenResetPassword = (user: User) => {
    setResetPasswordUser(user);
    setResetPasswordModalOpen(true);
  };

  const handleDelete = async (user: User) => {
    const confirmed = window.confirm(t('users.deleteConfirm', { email: user.email }));
    if (!confirmed) return;

    try {
      await deleteUser.mutateAsync(user.id);
      toast.success(t('users.userDeleted'));
    } catch {
      toast.error(t('users.userDeleteError'));
    }
  };

  const handleRoleFilterChange = (value: string) => {
    setRoleFilter(value);
    setPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleBetaFilterChange = (value: string) => {
    setBetaFilter(value);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('users.title')}</h1>
        <Button className="gap-2" onClick={handleOpenCreate}>
          <Plus className="h-4 w-4" />
          {t('users.createUser')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="max-w-sm flex-1">
          <SearchInput value={search} onChange={setSearch} />
        </div>
        <Select value={roleFilter} onValueChange={handleRoleFilterChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('users.filterByRole')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_FILTER_VALUE}>{t('common.all')} - {t('users.role')}</SelectItem>
            {Object.values(USER_ROLES).map((role) => (
              <SelectItem key={role} value={role}>
                {t(`users.roles.${role}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('users.filterByStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_FILTER_VALUE}>{t('common.all')} - {t('common.status')}</SelectItem>
            <SelectItem value="active">{t('common.active')}</SelectItem>
            <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={betaFilter} onValueChange={handleBetaFilterChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('users.beta.filter')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_FILTER_VALUE}>{t('common.all')} - {t('users.beta.badge')}</SelectItem>
            <SelectItem value="beta">{t('users.beta.onlyBeta')}</SelectItem>
            <SelectItem value="notBeta">{t('users.beta.notBeta')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('users.email')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('users.firstName')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('users.lastName')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('users.role')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('users.company')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.status')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('users.createdAt')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items.map((user) => (
                      <tr key={user.id} className="border-b hover:bg-muted/30 transition-colors">
                        <td className="p-4 text-sm">
                          <div className="flex items-center gap-1.5">
                            {user.email}
                            {user.emailVerified ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-sm">{user.firstName || '-'}</td>
                        <td className="p-4 text-sm">{user.lastName || '-'}</td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <Badge variant="outline">{t(`users.roles.${user.role}`)}</Badge>
                            {user.isBetaTester && <Badge variant="info">{t('users.beta.badge')}</Badge>}
                          </div>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{user.company?.name || '-'}</td>
                        <td className="p-4 text-center">
                          <StatusBadge status={user.status} />
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{formatDate(user.createdAt)}</td>
                        <td className="p-4">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenEdit(user)}
                              title={t('common.edit')}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenResetPassword(user)}
                              title={t('users.resetPassword')}
                            >
                              <KeyRound className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setNotificationUser(user);
                                setNotificationModalOpen(true);
                              }}
                              title="Send notification"
                            >
                              <Bell className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setTopUpUser(user);
                                setTopUpModalOpen(true);
                              }}
                              title={t('users.topUp.title')}
                              className="text-green-600 hover:text-green-700"
                            >
                              <Wallet className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(user)}
                              title={t('common.delete')}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {data?.items.length === 0 && (
                      <tr>
                        <td colSpan={8} className="p-8 text-center text-muted-foreground">
                          {t('common.noResults')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {data?.items.map((user) => (
                  <div key={user.id} className="p-4 space-y-2">
                    <div className="font-medium text-sm flex items-center gap-1.5">
                      {user.email}
                      {user.emailVerified ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {user.firstName || '-'} {user.lastName || '-'}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{t(`users.roles.${user.role}`)}</Badge>
                      {user.isBetaTester && <Badge variant="info">{t('users.beta.badge')}</Badge>}
                      <StatusBadge status={user.status} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{formatDate(user.createdAt)}</span>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenEdit(user)}
                          title={t('common.edit')}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenResetPassword(user)}
                          title={t('users.resetPassword')}
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            setNotificationUser(user);
                            setNotificationModalOpen(true);
                          }}
                          title="Send notification"
                        >
                          <Bell className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-green-600 hover:text-green-700"
                          onClick={() => {
                            setTopUpUser(user);
                            setTopUpModalOpen(true);
                          }}
                          title={t('users.topUp.title')}
                        >
                          <Wallet className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(user)}
                          title={t('common.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {data?.items.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    {t('common.noResults')}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {data?.pagination && (
        <Pagination
          page={page}
          totalPages={data.pagination.totalPages}
          onPageChange={setPage}
        />
      )}

      {/* Modals */}
      <UserModal
        open={userModalOpen}
        onOpenChange={setUserModalOpen}
        user={editingUser}
      />

      <ResetPasswordModal
        open={resetPasswordModalOpen}
        onOpenChange={setResetPasswordModalOpen}
        user={resetPasswordUser}
      />

      <SendNotificationModal
        open={notificationModalOpen}
        onOpenChange={setNotificationModalOpen}
        user={notificationUser}
      />

      <TopUpWalletModal
        open={topUpModalOpen}
        onOpenChange={setTopUpModalOpen}
        user={topUpUser}
      />
    </div>
  );
}
